import { useState, useEffect } from 'react';
import { Grid, Box, IconButton, Typography, Paper } from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import PageHeader from '../PageHeader/PageHeader';
import SummaryCards from '../Reports/SummaryCards';
import MonthYearSelector from '../Reports/Sales/MonthYearSelector';
import DashboardConfigModal from './DashboardConfigModal';

// Importar todos los componentes de gráficos
import SalesTrendChartStacked from '../Reports/Sales/SalesTrendChartStacked';
import BranchSalesChart from '../Reports/Sales/BranchSalesChart';
import BranchSalesTrendChart from '../Reports/Sales/BranchSalesTrendChart';
import TopProductsChart from '../Reports/Sales/TopProductsChart';
import SalesByChannelChart from '../Reports/Sales/SalesByChannelChart';
import PaymentMethodsChart from '../Reports/Sales/PaymentMethodsChart';
import SalesByHourChart from '../Reports/Sales/SalesByHourChart';
import CategorySalesChart from '../Reports/Sales/CategorySalesChart';
import SalesComparisonChart from '../Reports/Sales/SalesComparisonChart';
import AverageSalesByBranchChart from '../Reports/Sales/AverageSalesByBranchChart';
import SellerPerformanceTable from '../Reports/Sales/SellerPerformanceTable';
import BranchParticipationPieChart from '../Reports/Branch/BranchParticipationPieChart';
import DefinedVsUndefinedChart from '../Reports/Branch/DefinedVsUndefinedChart';
import AverageTicketByBranchChart from '../Reports/Branch/AverageTicketByBranchChart';
import PaymentMethodsByBranchChart from '../Reports/Branch/PaymentMethodsByBranchChart';
import BranchMonthlyGrowthChart from '../Reports/Branch/BranchMonthlyGrowthChart';
import StockValueByBranchChart from '../Reports/Inventory/StockValueByBranchChart';
import MonthlyProductionChart from '../Reports/Inventory/MonthlyProductionChart';

import DashboardService from '../../services/DashboardService';
import DashboardConfigService from '../../services/DashboardConfigService';
import { getDashboardEnabledCategories } from '../../utils/dashboardPermissions';
import {
  MonetizationOn as MonetizationOnIcon,
  Percent as PercentIcon,
  Receipt as ReceiptIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { formatMoney } from '../../utils/currency';

// Mapeo de componentes
const CHART_COMPONENTS = {
  SalesTrendChartStacked: SalesTrendChartStacked,
  BranchSalesChart: BranchSalesChart,
  BranchSalesTrendChart: BranchSalesTrendChart,
  TopProductsChart: TopProductsChart,
  SalesByChannelChart: SalesByChannelChart,
  PaymentMethodsChart: PaymentMethodsChart,
  SalesByHourChart: SalesByHourChart,
  CategorySalesChart: CategorySalesChart,
  SalesComparisonChart: SalesComparisonChart,
  AverageSalesByBranchChart: AverageSalesByBranchChart,
  SellerPerformanceTable: SellerPerformanceTable,
  BranchParticipationPieChart: BranchParticipationPieChart,
  DefinedVsUndefinedChart: DefinedVsUndefinedChart,
  AverageTicketByBranchChart: AverageTicketByBranchChart,
  PaymentMethodsByBranchChart: PaymentMethodsByBranchChart,
  BranchMonthlyGrowthChart: BranchMonthlyGrowthChart,
  StockValueByBranchChart: StockValueByBranchChart,
  MonthlyProductionChart: MonthlyProductionChart,
};

const CustomDashboard = () => {
  const { t } = useTranslation();
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [selectedCharts, setSelectedCharts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [isInitialized, setIsInitialized] = useState(false);
  const allowedCategories = getDashboardEnabledCategories();

  const filterChartsByAccess = (charts = []) =>
    charts.filter((chart) => allowedCategories.includes(chart.category));

  // Primero cargar la configuración, luego los datos
  useEffect(() => {
    const initialize = async () => {
      await loadConfig();
      // Esperar a que la configuración se cargue antes de cargar datos
      await fetchDashboardData();
      setIsInitialized(true);
    };
    initialize();
  }, []);

  // Cargar datos cuando cambia el mes/año (pero no en el mount inicial)
  useEffect(() => {
    if (isInitialized) {
      fetchDashboardData();
    }
  }, [month, year, isInitialized]);

  const loadConfig = async () => {
    try {
      const config = await DashboardConfigService.getDashboardConfig();
      setSelectedCharts(filterChartsByAccess(config.charts || []));
    } catch (error) {
      console.error('Error loading dashboard config:', error);
      // Si hay error o no existe configuración, usar array vacío
      setSelectedCharts([]);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const data = await DashboardService.getDashboardData({
        month,
        year,
        allowedCategories
      });
      setDashboardData(data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setDashboardData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (charts) => {
    try {
      const sanitizedCharts = filterChartsByAccess(charts);
      // Guardar configuración
      await DashboardConfigService.saveDashboardConfig(sanitizedCharts);
      setSelectedCharts(sanitizedCharts);
      // No es necesario recargar datos, solo la configuración cambió
    } catch (error) {
      console.error('Error saving dashboard config:', error);
      throw error; // Re-lanzar para que el modal pueda manejar el error
    }
  };

  const getDataForChart = (chart) => {
    if (!dashboardData) return null;
    
    switch (chart.category) {
      case 'sales':
        return dashboardData.sales;
      case 'branch':
        return dashboardData.branch;
      case 'inventory':
        return dashboardData.inventory;
      default:
        return null;
    }
  };

  const renderChart = (chart) => {
    const Component = CHART_COMPONENTS[chart.component];
    if (!Component) {
      return (
        <Paper key={chart.id} sx={{ p: 2 }}>
          <Typography color="error">
            Componente {chart.component} no encontrado
          </Typography>
        </Paper>
      );
    }

    const data = getDataForChart(chart);
    const props = getChartProps(chart, data);

    return (
      <Grid item {...chart.defaultSize} key={chart.id}>
        <Component {...props} loading={loading} />
      </Grid>
    );
  };

  const getChartProps = (chart, data) => {
    const baseProps = { data, loading };
    
    switch (chart.component) {
      case 'SalesTrendChartStacked':
      case 'BranchSalesTrendChart':
        return {
          ...baseProps,
          data: data?.branchSalesTrend,
          branches: data?.branches,
        };
      case 'BranchSalesChart':
        return {
          ...baseProps,
          data: data?.branchSales,
          branches: data?.branches,
        };
      case 'TopProductsChart':
        return { ...baseProps, data: data?.topProducts || [] };
      case 'SalesByChannelChart':
        return { ...baseProps, data: data?.salesByChannel || [] };
      case 'PaymentMethodsChart':
        return { ...baseProps, data: data?.paymentMethods || [] };
      case 'SalesByHourChart':
        return { ...baseProps, data: data?.salesByHour || [] };
      case 'CategorySalesChart':
        return { ...baseProps, data: data?.salesByCategory || [] };
      case 'SalesComparisonChart':
        return { ...baseProps, data: data?.salesComparison };
      case 'AverageSalesByBranchChart':
        return {
          ...baseProps,
          data: data?.averageSalesByBranch || [],
          branches: data?.branches,
        };
      case 'SellerPerformanceTable':
        return { ...baseProps, data: data?.performanceBySeller };
      case 'BranchParticipationPieChart':
        return {
          ...baseProps,
          data: data?.branchSalesDistribution || [],
          branches: data?.branches || [],
        };
      case 'DefinedVsUndefinedChart':
        return {
          ...baseProps,
          data: data?.definedVsUndefined || [],
          branches: data?.branches || [],
        };
      case 'AverageTicketByBranchChart':
        return {
          ...baseProps,
          data: data?.averageSalesByBranch || [],
          branches: data?.branches || [],
        };
      case 'PaymentMethodsByBranchChart':
        return {
          ...baseProps,
          data: data?.paymentMethodsByBranch || [],
          branches: data?.branches || [],
        };
      case 'BranchMonthlyGrowthChart': {
        // Este gráfico necesita datos transformados
        const transformMonthlyGrowthData = (monthlyGrowth) => {
          if (!monthlyGrowth || !Array.isArray(monthlyGrowth)) return [];
          return monthlyGrowth.map(item => ({
            month: item.month,
            ...item.values
          }));
        };
        return {
          ...baseProps,
          data: transformMonthlyGrowthData(data?.monthlyGrowth || []),
          branches: data?.branches || [],
        };
      }
      case 'StockValueByBranchChart':
        return {
          ...baseProps,
          branches: data?.branches || [],
          data: data?.stockValueByBranch || {},
        };
      case 'MonthlyProductionChart':
        return { ...baseProps, data: data?.producedByMonth || [] };
      default:
        return baseProps;
    }
  };

  const summaryItems = dashboardData?.sales
    ? [
        {
          title: t('total_sales_month') || 'Ventas del mes',
          icon: <MonetizationOnIcon color="primary" fontSize="large" />,
          value: `${formatMoney(dashboardData.sales.summary?.totalAmount ?? 0)}`,
        },
        {
          title: t('tickets_month') || 'Tickets del mes',
          icon: <ReceiptIcon color="success" fontSize="large" />,
          value: dashboardData.sales.summary?.ticketCount || 0,
        },
        {
          title: t('average_ticket') || 'Ticket promedio',
          icon: <BarChartIcon color="info" fontSize="large" />,
          value: `${formatMoney(dashboardData.sales.summary?.averageTicket ?? 0)}`,
        },
        {
          title: t('discount_percentage') || 'Descuentos',
          icon: <PercentIcon color="secondary" fontSize="large" />,
          value: `${dashboardData.sales.summary?.discountPercent?.toFixed(1) || 0}%`,
        },
      ]
    : [];

  return (
    <Box p={3} mb={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <PageHeader
          title={t('dashboard') || 'Dashboard'}
          subtitle={t('dashboard_subtitle') || 'Resumen general del sistema'}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <MonthYearSelector month={month} setMonth={setMonth} year={year} setYear={setYear} />
          <IconButton
            onClick={() => setConfigModalOpen(true)}
            color="primary"
            title={t('customize_dashboard') || 'Personalizar Dashboard'}
          >
            <SettingsIcon />
          </IconButton>
        </Box>
      </Box>

      <Box mt={2}>
        <SummaryCards items={summaryItems} loading={loading} />
      </Box>

      {selectedCharts.length === 0 ? (
        <Paper sx={{ p: 4, mt: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('no_charts_selected') || 'No hay gráficos seleccionados'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('configure_dashboard_message') || 'Haz clic en el icono de configuración para personalizar tu dashboard'}
          </Typography>
          <IconButton
            onClick={() => setConfigModalOpen(true)}
            color="primary"
            size="large"
          >
            <SettingsIcon fontSize="large" />
          </IconButton>
        </Paper>
      ) : (
        <Grid container spacing={2} mt={2}>
          {selectedCharts.map(renderChart)}
        </Grid>
      )}

      <DashboardConfigModal
        open={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        onSave={handleSaveConfig}
        selectedCharts={selectedCharts}
      />
    </Box>
  );
};

export default CustomDashboard;
